/**
 * AdminSheet
 *
 * Displays the four admin tool rows (Identity verifications, Latest users,
 * Feedback, Transactions) extracted from Flutter's `AdminScreen`. The
 * Transactions row opens the wallet page in its admin variant
 * (`Navigator.navigateToWallet({ isFromAdmin: true })`, mirroring Flutter's
 * `_onItemTap` → `case AdminItemType.transactions`); the other three rows
 * (Identity verifications, Latest users, Feedback) remain placeholders and open
 * the shared "Not implemented yet" InfoSheet.
 *
 * Flutter source: lib/presentation/screens/admin/admin_screen.dart (the
 * `_items` list + list rendering, lines 34-172). On web this is a bottom
 * sheet rather than Flutter's full `Scaffold` screen — a deliberate, allowed
 * UI deviation; the sheet's drag indicator replaces Flutter's AppBar + back
 * button, matching the sibling `HelpAboutSheet` / `AccountInfoSheet`.
 */

import { SheetService } from '../service/SheetService';
import { AdminContent } from './components/AdminContent';

/**
 * Configuration for showing the Admin sheet
 */
export interface AdminSheetConfig {
  /** Optional container element, screen key string, or null */
  container?: HTMLElement | string | null;

  /** Optional screen key of the route opening this sheet */
  screenKey?: string;

  /** Force the sheet to render as a dialog instead of a bottom sheet */
  forceDialog?: boolean;
}

/**
 * Show an Admin sheet.
 *
 * @param config - Configuration for the sheet
 * @returns Promise that resolves when the sheet is dismissed
 *
 * @example
 * await AdminSheet.show({ screenKey });
 */
const show = (config: AdminSheetConfig = {}): Promise<void> => {
  return new Promise((resolve) => {
    SheetService.show({
      content: <AdminContent screenKey={config.screenKey} />,
      onClose: resolve,
      closeOnClickOutside: true,
      container: config.container,
      screenKey: config.screenKey,
      forceDialog: config.forceDialog,
      qaId: 'admin',
    });
  });
};

export const AdminSheet = {
  show,
};
